import React, { useEffect, useRef, useState } from 'react'
import { Select } from 'antd'
import { useTranslation } from 'react-i18next'
import styles from './results-section.module.scss'
import AlertComponent from '@BUP/components/Generics/Alert'
import BupSpinner from '@BUP/components/Loading/Spinner'
import ButtonComponent from '@BUP/components/Generics/Buttons/button-component'
import CourseCard from './CourseCard'
import { useGetResultVersions } from '@BUP/hooks/CourseAssignment/useGetResultVersions'
import { useGetResultVersion } from '@BUP/hooks/CourseAssignment/useGetResultVersion'
import { useUploadResultXlsx } from '@BUP/hooks/CourseAssignment/useUploadResultXlsx'
import { useSaveResultEdits } from '@BUP/hooks/CourseAssignment/useSaveResultEdits'
import { computeCourseMetrics } from '@BUP/utils/courseAssignmentResults'

const { Option } = Select

/**
 * SDD Paso 6-8: upload course-generator's result as a new version, pick a version, see the
 * resulting course table, and drag & drop students between courses.
 *
 * Upload accepts the `.xlsx` files course-generator's "local mode" run produces (one per course,
 * e.g. "Curso 1 (detalle).xlsx") - not a JSON file. course-generator has no stable hosted API
 * (confirmed: it's operated by running the script locally, per its own `MODE = 'local'` default),
 * so a JSON upload path would have no real file to point it at. A counselor can select every file
 * from the run's "Búsqueda Local" output folder - files not named like "Curso N..." are ignored
 * server-side, so there's no need to hand-pick just the course files.
 *
 * A drag only updates a local "working copy" (live-recomputed counts/%/balance via
 * computeCourseMetrics, same formula the backend uses) until "Guardar cambios" POSTs the
 * accumulated moves - which creates a brand new version server-side rather than mutating the one
 * being viewed (see createEditedVersion).
 */
const ResultsSection = ({ periodId }) => {
  const [t] = useTranslation('translation', { keyPrefix: 'pages.data.courseAssignmentPeriodDashboard' })
  const fileInputRef = useRef(null)
  const [uploadError, setUploadError] = useState(null)
  const [selectedVersionId, setSelectedVersionId] = useState(null)
  const [workingCourses, setWorkingCourses] = useState(null)
  const [pendingMoves, setPendingMoves] = useState(new Map()) // resultStudentId -> targetCourseName

  const { versions, isLoading: isLoadingVersions, refetch: refetchVersions } = useGetResultVersions(periodId)
  const { mutateAsync: uploadResultXlsx, isPending: isUploading } = useUploadResultXlsx()
  const { version, isLoading: isLoadingVersion, error: errorVersion } = useGetResultVersion(periodId, selectedVersionId)
  const { mutateAsync: saveResultEdits, isPending: isSaving } = useSaveResultEdits()

  useEffect(() => {
    if (!selectedVersionId && versions.length) {
      setSelectedVersionId(versions[0].id)
    }
  }, [versions, selectedVersionId])

  // Reset the editable working copy whenever a different version loads (including right after
  // saving edits, once the new version comes back from the server).
  useEffect(() => {
    setWorkingCourses(version?.courses ?? null)
    setPendingMoves(new Map())
  }, [version])

  const handleFileChange = async (event) => {
    const files = event.target.files
    if (!files || !files.length) return
    setUploadError(null)

    try {
      const result = await uploadResultXlsx({ periodId, files })
      setSelectedVersionId(result.id)
      refetchVersions()
    } catch (error) {
      setUploadError(error?.response?.data?.message || t('uploadGenericError'))
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleDropStudent = (resultStudentId, targetCourseName) => {
    // A drop back onto the student's own current course is a no-op - the whole CourseCard is the
    // drop zone (not just its edges), so this is easy to trigger by accident. Skip it before
    // touching either piece of state, otherwise it still records a "pending move" that maps the
    // student to the course they're already in - nothing visibly changes, but the Guardar/
    // Descartar buttons appear and saving would create a new result version for no real edit.
    const sourceCourse = workingCourses.find(course => course.students.some(s => String(s.id) === resultStudentId))
    if (!sourceCourse || sourceCourse.name === targetCourseName) return

    setWorkingCourses(currentCourses => {
      let movedStudent = null
      const withoutStudent = currentCourses.map(course => {
        const student = course.students.find(s => String(s.id) === resultStudentId)
        if (!student) return course
        movedStudent = student
        const students = course.students.filter(s => String(s.id) !== resultStudentId)
        return { ...course, students, ...computeCourseMetrics(students) }
      })

      if (!movedStudent) return currentCourses
      return withoutStudent.map(course => {
        if (course.name !== targetCourseName) return course
        const students = [...course.students, movedStudent]
        return { ...course, students, ...computeCourseMetrics(students) }
      })
    })

    setPendingMoves(current => {
      const next = new Map(current)
      next.set(resultStudentId, targetCourseName)
      return next
    })
  }

  const handleDiscardChanges = () => {
    setWorkingCourses(version?.courses ?? null)
    setPendingMoves(new Map())
  }

  const handleSaveChanges = async () => {
    const moves = [...pendingMoves.entries()].map(([resultStudentId, targetCourseName]) => ({
      resultStudentId,
      targetCourseName
    }))
    const newVersion = await saveResultEdits({ periodId, versionId: selectedVersionId, moves })
    setSelectedVersionId(newVersion.id)
    refetchVersions()
  }

  const hasPendingMoves = pendingMoves.size > 0

  return (
    <div className={styles.container}>
      <div className={styles.upload_wrapper}>
        <p>{t('resultUploadDescription')}</p>
        <div className={styles.controls_wrapper}>
          <input
            ref={fileInputRef}
            type='file'
            accept='.xlsx'
            multiple
            onChange={handleFileChange}
            disabled={isUploading}
          />
        </div>
        {isUploading && <BupSpinner size='small' />}
        {uploadError && <p className={styles.error_text}>{uploadError}</p>}
      </div>

      {isLoadingVersions
        ? <BupSpinner size='small' />
        : versions.length === 0
          ? <p>{t('resultNoVersionsYet')}</p>
          : (
            <>
              <div className={styles.controls_wrapper}>
                <Select
                  className={styles.version_select}
                  value={selectedVersionId}
                  onChange={setSelectedVersionId}
                  disabled={hasPendingMoves}
                >
                  {versions.map(v => (
                    <Option key={v.id} value={v.id}>
                      {t('resultVersionLabel', { number: v.versionNumber })}
                    </Option>
                  ))}
                </Select>
              </div>

              {isLoadingVersion && <BupSpinner size='small' />}
              {errorVersion && (
                <AlertComponent
                  type='error'
                  messageKey='common.resources.course-assignment.actions.read.failure'
                  error={errorVersion}
                />
              )}
              {version && workingCourses && (
                <>
                  {version.unmatchedStudentNames?.length > 0 && (
                    <AlertComponent
                      type='warning'
                      message={t('resultUnmatchedNames', { names: version.unmatchedStudentNames.join(', ') })}
                    />
                  )}
                  <p>{t('resultDragHint')}</p>
                  {workingCourses.map(course => (
                    <CourseCard
                      key={course.id}
                      course={course}
                      isEditable
                      onDropStudent={handleDropStudent}
                    />
                  ))}
                  {hasPendingMoves && (
                    <div className={styles.controls_wrapper}>
                      <ButtonComponent
                        buttonText={t('resultSaveChangesButton')}
                        colorType='primary'
                        onClick={handleSaveChanges}
                        disabled={isSaving}
                      />
                      <ButtonComponent
                        buttonText={t('resultDiscardChangesButton')}
                        colorType='tertiary'
                        onClick={handleDiscardChanges}
                        disabled={isSaving}
                      />
                    </div>
                  )}
                </>
              )}
            </>
            )}
    </div>
  )
}

export default ResultsSection
